"use client";

import React, { forwardRef, useMemo } from "react";
import type { SpinningWheelUIReward } from "@cede/types";
import { CedeIconCircle } from "@/components/icons";
import { WHEEL_ANIMATION_DERIVED } from "@/constants/spinningWheel";
import { getRewardIcon } from "@/utils/wheel";
import { getIconCountForAmount } from "@/utils/wheel/spinningWheel";

// Extend CSSProperties to include CSS custom properties
interface ExtendedCSSProperties extends React.CSSProperties {
  [key: `--${string}`]: string | number;
}

interface WheelCanvasProps {
  /** Array of rewards to display on the wheel */
  rewards: SpinningWheelUIReward[];
  /** Current rotation angle in degrees */
  rotation: number;
  /** Whether the wheel is currently spinning */
  isSpinning: boolean;
  /** The winning reward (for glow effect) */
  winningReward: SpinningWheelUIReward | null;
  /** Whether rewards are still loading */
  isLoading: boolean;
  /** Wheel diameter in pixels */
  diameter?: number;
}

/**
 * Optimized wheel canvas component with memoized segments and animations
 * Handles the visual rendering of the spinning wheel
 */
export const WheelCanvas = React.memo(
  forwardRef<HTMLDivElement, WheelCanvasProps>(
    ({ rewards, rotation, isSpinning, winningReward, isLoading, diameter = 400 }, ref) => {
      // Memoized calculations
      const wheelMetrics = useMemo(() => {
        const radius = diameter / 2;
        const numberOfItems = rewards.length;

        return {
          radius,
          numberOfItems,
          segmentAngle: 360 / numberOfItems,
          centerSize: diameter * 0.25,
          iconScale: (diameter / 400) * 3.6,
        };
      }, [diameter, rewards.length]);

      // Wheel styles with CSS custom properties
      const wheelStyles: ExtendedCSSProperties = {
        "--nb-item": wheelMetrics.numberOfItems,
        "width": `${diameter}px`,
        "height": `${diameter}px`,
        "backgroundColor": "transparent",
        "transition": isSpinning ? WHEEL_ANIMATION_DERIVED.SPIN_TRANSITION : "none",
      };

      // Outer ring with contrasting color
      const outerRing = {
        size: diameter + 24,
        background: "var(--color-cede-primary-dark)",
        boxShadow: "0 0 30px color-mix(in srgb, var(--color-cede-primary-dark) 50%, transparent)",
      };

      // Segment custom style function
      const getSegmentStyle = (index: number): ExtendedCSSProperties => ({
        "--item-nb": index,
      });

      // Icon rendering function
      const renderIcons = (reward: SpinningWheelUIReward) => {
        if (reward.rewardAmount.lessThan(0)) return null;

        const iconSize = 20;
        const IconComponent = getRewardIcon(reward.rewardSymbol, { width: iconSize, height: iconSize });

        // Special case for < 1 prizes
        if (reward.rewardAmount.lessThan(1)) {
          return (
            <div className="flex justify-center items-center" style={{ height: `${iconSize}px` }}>
              <div style={{ filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.9)) grayscale(0.5) opacity(0.7)" }}>
                {IconComponent}
              </div>
            </div>
          );
        }

        // Determine number of icons based on reward amount
        const iconCount = getIconCountForAmount(reward.rewardAmount);

        if (iconCount <= 3) {
          // Individual icons for small amounts
          return (
            <div className="flex justify-center items-center" style={{ height: `${iconSize}px` }}>
              {Array.from({ length: iconCount }, (_, i) => (
                <div
                  key={i}
                  style={{
                    marginLeft: i > 0 ? "2px" : "0",
                    filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.9))",
                  }}
                >
                  {IconComponent}
                </div>
              ))}
            </div>
          );
        } else {
          // Overlapping icons for larger amounts
          return (
            <div className="flex justify-center items-center" style={{ height: `${iconSize}px` }}>
              {Array.from({ length: iconCount }, (_, i) => (
                <div
                  key={i}
                  style={{
                    marginLeft: i > 0 ? "-8px" : "0",
                    zIndex: iconCount - i,
                    position: "relative",
                    filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.9))",
                  }}
                >
                  {IconComponent}
                </div>
              ))}
            </div>
          );
        }
      };

      return (
        <div className="relative">
          {/* Outer Ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: `${outerRing.size}px`,
              height: `${outerRing.size}px`,
              background: outerRing.background,
              boxShadow: outerRing.boxShadow,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />

          {/* Main Wheel */}
          <div
            ref={ref}
            className="wheel relative block overflow-hidden rounded-full cursor-pointer"
            style={{
              ...wheelStyles,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
            }}
          >
            {/* Wheel Segments */}
            {rewards.map((reward, index) => {
              const isWinning = winningReward?.id === reward.id;

              return (
                <div
                  key={reward.id}
                  className={`wheel-item absolute block box-border top-1/2 left-1/2 w-1/2 text-white font-bold ${
                    isWinning ? "winning-segment-glow" : ""
                  }`}
                  style={{
                    ...getSegmentStyle(index),
                    transformOrigin: "center left",
                    transform: "translateY(-50%) rotate(calc(var(--item-nb) * (360deg / var(--nb-item))))",
                    color: "white",
                    padding: "0",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {/* Segment Background */}
                  <div
                    className="absolute inset-0 -z-10"
                    style={{
                      width: "0",
                      height: "0",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      borderTop: `${
                        wheelMetrics.radius * Math.tan(Math.PI / wheelMetrics.numberOfItems)
                      }px solid transparent`,
                      borderBottom: `${
                        wheelMetrics.radius * Math.tan(Math.PI / wheelMetrics.numberOfItems)
                      }px solid transparent`,
                      borderLeft: "0 solid transparent",
                      borderRight: `${wheelMetrics.radius}px solid ${reward.color}`,
                    }}
                  />

                  {/* Reward Content */}
                  <div
                    className="relative z-10 flex flex-col items-center justify-center gap-1"
                    style={{
                      marginRight: "20px",
                      transform: "rotate(90deg)",
                    }}
                  >
                    {/* Icons */}
                    {!isLoading && renderIcons(reward)}

                    {/* Label */}
                    <span
                      className="block leading-tight font-bold text-white whitespace-nowrap"
                      style={{
                        textShadow:
                          "0 2px 8px rgba(0,0,0,0.8), 0 1px 0 rgba(0,0,0,1), 1px 0 0 rgba(0,0,0,1), -1px 0 0 rgba(0,0,0,1), 0 -1px 0 rgba(0,0,0,1)",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                        letterSpacing: "0.5px",
                        fontSize: "14px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                      }}
                    >
                      {isLoading ? (
                        <div className="bg-white/30 rounded animate-pulse" style={{ width: "50px", height: "12px" }} />
                      ) : (
                        `${reward.rewardAmount.toNumber()} ${reward.rewardSymbol}`
                      )}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Center Icon */}
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 cursor-pointer hover:scale-105 transition-transform duration-200 flex items-center justify-center"
              style={{
                width: `${wheelMetrics.centerSize}px`,
                height: `${wheelMetrics.centerSize}px`,
              }}
            >
              <div
                style={{
                  transform: `scale(${wheelMetrics.iconScale})`,
                  transformOrigin: "center center",
                }}
              >
                <CedeIconCircle width={22} height={22} />
              </div>
            </div>
          </div>

          {/* CSS Animations */}
          <style jsx>{`
            .winning-segment-glow {
              animation: winningGlow 2s ease-in-out infinite;
            }

            @keyframes winningGlow {
              0%,
              100% {
                box-shadow:
                  0 0 15px var(--color-cede-primary),
                  0 0 30px var(--color-cede-primary) 80,
                  0 0 45px var(--color-cede-primary) 40;
                filter: drop-shadow(0 0 10px var(--color-cede-primary)) brightness(1.1);
              }
              50% {
                box-shadow:
                  0 0 25px var(--color-cede-primary),
                  0 0 50px var(--color-cede-primary) CC,
                  0 0 75px var(--color-cede-primary) 80;
                filter: drop-shadow(0 0 20px var(--color-cede-primary)) brightness(1.3);
              }
            }
          `}</style>
        </div>
      );
    },
  ),
);

WheelCanvas.displayName = "WheelCanvas";
